import * as tfc from '@tensorflow/tfjs-core';
import * as blazeface from '@tensorflow-models/blazeface';
import * as tfjsWasm from '@tensorflow/tfjs-backend-wasm';

import FaceDetection from '../../helpers/FaceDetection';
import { calculateTemperatureByThermalData } from '../../helpers/dl_temperature_measurement';
import { thumbnailFullTransformation } from '../../constants/detection';
import { detectBlackBody } from '../../helpers/dl_black_body_detection';

const OpenCvLoadedTimeoutInMs = 100;
const returnTensors = false;
const annotateBoxes = true;

export const load = async () => {
  tfjsWasm.setWasmPath('/tfjs-backend-wasm.wasm');
};

export const init = async (data) => {
  const { backend, faceDetectionSettings } = data;

  await tfc.setBackend(backend);
  const [faceModel] = await Promise.all([
    blazeface.load({
      // The maximum number of faces returned by the model
      maxFaces: faceDetectionSettings.maxFaces,
      // The width of the input image
      inputWidth: faceDetectionSettings.faceDetectionImageMinWidth,
      // The height of the input image
      inputHeight: faceDetectionSettings.faceDetectionImageMinHeight,
      // The threshold for deciding whether boxes overlap too much
      iouThreshold: faceDetectionSettings.iouThreshold,
      // The threshold for deciding when to remove boxes based on score
      scoreThreshold: faceDetectionSettings.scoreThreshold,
    }),
    isOpenCvLoaded(),
  ]);

  return faceModel;
};

export const detectBlackBodyByThermalData = async (data) => {
  const { thermalData } = data;

  const bbDetection = await detectBlackBody(thermalData);
  return bbDetection;
};

export const measureTemperatureByThermalData = async (data, faceModel) => {
  const { thermalData, bbCoordinates, faceDetectionSettings } = data;

  const temperatureData = await calculateTemperatureByThermalData(
    thermalData,
    faceModel,
    bbCoordinates,
    faceDetectionSettings.faceDetectionImageMinWidth,
    faceDetectionSettings.faceDetectionImageMinHeight,
    thumbnailFullTransformation[faceDetectionSettings.faceDetectionImageMinWidth],
  );
  if (temperatureData.predictions) {
    const faceDetection = new FaceDetection();
    const imageRatio = faceDetection.calculateFaceDetectionImageRatio(
      thermalData.width,
      thermalData.height,
      faceDetectionSettings.faceDetectionImageMinWidth,
      faceDetectionSettings.faceDetectionImageMinHeight,
    );
    temperatureData.predictions = faceDetection.normalizePredictions(
      temperatureData.predictions,
      returnTensors,
      annotateBoxes,
      imageRatio.faceDetectionImageRatio,
      thermalData.width,
      thermalData.height,
    );
  }
  return temperatureData;
};

export const isOpenCvLoaded = () => {
  let timeoutId;

  const resolveOpenCvPromise = (resolve) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    try {
      // eslint-disable-next-line no-undef
      if (cv && cv.Mat) {
        return resolve();
      } else {
        timeoutId = setTimeout(() => {
          resolveOpenCvPromise(resolve);
        }, OpenCvLoadedTimeoutInMs);
      }
    } catch {
      timeoutId = setTimeout(() => {
        resolveOpenCvPromise(resolve);
      }, OpenCvLoadedTimeoutInMs);
    }
  };

  return new Promise((resolve) => {
    resolveOpenCvPromise(resolve);
  });
};
